import { useEffect, useMemo } from 'react';

import {
    bookingDraftAtom,
    updateDraftAtom,
} from '@/features/booking/shared/model/bookingState';
import { R } from '@/features/booking/resources/Texts.resx';
import { Log } from '@/shared/Log';
import { ConsultationTypeEnum } from '@/shared/enums';
import { useMutation } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { Utils } from '@/shared/Utils';
import {
    QueryKeys,
    useUrlState,
} from '@/features/booking/shared/model/useUrlState';
import { fetchAiNurseQuestion } from '@/features/booking/widgets/describe-problem/api/fetchAiNurseQuestion';
import { AiNurseStep } from '@/features/booking/shared/model/types';
import { ProductDto } from '@/shared/interfaces';

export interface AiNurseReturn {
    isEnabled: boolean;
    getHeader: () => string;
    getDescription: () => string;
    showAiNurse: boolean;
    showAiNurseModal: boolean;
    showQuestion: boolean;
    showOverview: boolean;
    suggestAttachment: boolean;
    openIfNecessary: (subject: string, message: string) => Promise<boolean>;
    skip: () => void;
    closeModal: () => void;
    goToQuestion: () => void;
    goToOverview: () => void;
    submitAnswer: (updatedMessage: string) => void;
    isAskingAiNurse: boolean;
}

const parseAiNurseStep = (value?: string): AiNurseStep | undefined => {
    if (!value) return undefined;

    return Object.values(AiNurseStep).includes(value as AiNurseStep)
        ? (value as AiNurseStep)
        : undefined;
};

const cultureQuery = Utils.getParameterByName('culture');
const cultureCookie = decodeURIComponent(
    Utils.getCookie('.AspNetCore.Culture') ?? '',
);

export const useAiNurse = (product: ProductDto): AiNurseReturn => {
    const draftState = useAtomValue(bookingDraftAtom);
    const updateDraftState = useSetAtom(updateDraftAtom);
    const { urlState, updateUrl } = useUrlState();
    const isDoctorMessageConsultation =
        product.ConsultationType === ConsultationTypeEnum.Message;

    const disabledSteps = [
        AiNurseStep.Completed,
        AiNurseStep.Skipped,
        AiNurseStep.Disabled,
    ];
    const activeSteps = [AiNurseStep.Question, AiNurseStep.Overview];

    const aiNurseStep = useMemo(() => {
        const value = urlState.aiNurse;
        return parseAiNurseStep(value);
    }, [urlState.aiNurse]);

    const isDisabled = useMemo(() => {
        return disabledSteps.includes(
            parseAiNurseStep(draftState?.aiNurseStep),
        );
    }, [disabledSteps, draftState?.aiNurseStep]);

    const isEnabled = isDoctorMessageConsultation && !isDisabled;

    const showAiNurse =
        isEnabled &&
        aiNurseStep !== undefined &&
        activeSteps.includes(aiNurseStep);

    const submitAnswer = async (updatedMessage: string) => {
        Log.info('AI Nurse: user submitted answers');

        updateDraftState({
            message: updatedMessage,
            aiNurseStep: AiNurseStep.Completed,
            aiNurseQuestion: null,
            aiNurseAnswer: null,
            aiNurseSuggestAttachment: null,
        });

        updateUrl({ ...urlState, aiNurse: 'completed' }, true);
    };

    const skip = () => {
        Log.info(
            `AI Nurse: user skipped at step ${urlState[QueryKeys.AiNurse]}`,
        );

        updateDraftState({
            aiNurseStep: AiNurseStep.Skipped,
            aiNurseQuestion: null,
            aiNurseAnswer: null,
            aiNurseSuggestAttachment: null,
        });

        updateUrl({ ...urlState, aiNurse: AiNurseStep.Skipped }, true);
    };

    const closeModal = () => {
        updateDraftState({ aiNurseStep: AiNurseStep.Skipped });
        Log.info('AI Nurse: user closed modal');
    };

    const goToQuestion = () => {
        Log.info('AI Nurse: user continued to see the questions');
        setUrlStep(AiNurseStep.Question);
    };

    const goToOverview = () => {
        Log.info('AI Nurse: user continued to see overview');
        setUrlStep(AiNurseStep.Overview);
    };

    const setUrlStep = (newStep: AiNurseStep, replace: boolean = true) => {
        updateUrl({ ...urlState, [QueryKeys.AiNurse]: newStep }, replace);
    };

    const { mutateAsync, isPending } = useMutation({
        mutationFn: fetchAiNurseQuestion,
        onError: (error) => {
            if (error?.name === 'AbortError') {
                Log.info('AI nurse: timed out');
            } else {
                Log.info('AI nurse: failed. Error: ', error);
            }
        },
    });

    const openIfNecessary = async (subject: string, message: string) => {
        try {
            const response = await mutateAsync({
                Subject: subject,
                Message: message,
                HasAttachments: !!draftState?.attachmentUids?.length,
                IsChild: draftState?.isParentChosen === false.toString(),
                IsEnglish:
                    cultureQuery == 'en' || cultureCookie == 'c=en|uic=en',
            });

            if (response?.NeedsFollowup) {
                updateDraftState({
                    aiNurseQuestion: response.Questions,
                    aiNurseSuggestAttachment: response.SuggestAttachment
                        ? 'true'
                        : null,
                });
                Log.info(
                    `AI Nurse: modal opened. Questions fetched:\n\n${response.Questions}`,
                );
                setUrlStep(AiNurseStep.Modal, false);
                return true;
            }

            return false;
        } catch (error) {
            return false;
        }
    };

    const getHeader = () => {
        switch (aiNurseStep) {
            case AiNurseStep.Question:
                return R.AiNurseQuestionsHeader;
            case AiNurseStep.Overview:
                return R.AiNurseOverviewHeader;
            default:
                return '';
        }
    };

    const getDescription = () => {
        switch (aiNurseStep) {
            case AiNurseStep.Overview:
                return R.AiNurseOverviewDescription;
            default:
                return '';
        }
    };

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [aiNurseStep]);

    return {
        isEnabled,
        getHeader,
        getDescription,
        showAiNurse,
        showAiNurseModal: isEnabled && aiNurseStep === AiNurseStep.Modal,
        showQuestion: isEnabled && aiNurseStep === AiNurseStep.Question,
        showOverview: isEnabled && aiNurseStep === AiNurseStep.Overview,
        suggestAttachment: draftState?.aiNurseSuggestAttachment === 'true',
        openIfNecessary,
        skip,
        closeModal,
        goToQuestion,
        goToOverview,
        submitAnswer,
        isAskingAiNurse: isEnabled && isPending,
    };
};
